// filtri/registro.ts — i filtri presenti nel codice dell'app (cartelle catalogo/NNN-id/), caricati solo quando servono.
// Il server dice quali sono attivi, in che piano e a che prezzo (servizi/filtri.ts); qui c'è il codice.
import type { CreaFiltro, ManifestFiltro, RisorseFiltro } from "./tipi.ts";

const MODULI = import.meta.glob<{ default: CreaFiltro }>("./catalogo/*/filtro.ts");
const MANIFESTI = import.meta.glob<{ default: ManifestFiltro }>("./catalogo/*/manifest.json");
// Solo gli indirizzi dei file (piccoli): l'immagine vera si scarica quando il filtro parte.
const RISORSE = import.meta.glob<string>("./catalogo/*/risorse/*", { eager: true, query: "?url", import: "default" });

// Cartelle: NNN-id (1–159), ANNN-id (serie A: 1000 + n), BNNN-id (serie B: 2000 + n).
const CARTELLA = /^\.\/catalogo\/([AB]?)(\d{3})-([a-z0-9-]+)\/filtro\.ts$/;
const SERIE: Record<string, number> = { "": 0, A: 1000, B: 2000 };
// Nomi e descrizioni di tutti i filtri, un file per lingua (generato da scripts/genera-indice-filtri.mjs).
const TESTI_INDICE = import.meta.glob<{ default: Record<string, [string, string]> }>("./indice/testi.*.json");
/** Tutte le icone in un file solo (<symbol id="f-<id>">), da usare con <svg><use href={`${ICONE}#f-id`}/></svg>. */
export { default as ICONE } from "./indice/icone.svg?url";

export interface FiltroNelCodice {
  id: string;
  numero: number;
  cartella: string; // "./catalogo/001-baffi-classici"
}

/** I filtri che l'app sa disegnare, in ordine di catalogo. */
export const FILTRI_NEL_CODICE: readonly FiltroNelCodice[] = Object.keys(MODULI)
  .map((k) => {
    const m = CARTELLA.exec(k);
    return m ? { id: m[3]!, numero: SERIE[m[1]!]! + Number(m[2]), cartella: k.slice(0, k.lastIndexOf("/")) } : null;
  })
  .filter((x): x is FiltroNelCodice => x !== null)
  .sort((a, b) => a.numero - b.numero);

const perId = new Map(FILTRI_NEL_CODICE.map((f) => [f.id, f]));
export const presenteNelCodice = (id: string): boolean => perId.has(id);

export interface FiltroCaricato {
  crea: CreaFiltro;
  manifest: ManifestFiltro;
  risorse: RisorseFiltro;
}

function risorseDi(cartella: string): RisorseFiltro {
  return {
    immagine: async (nome: string) => {
      const url = RISORSE[`${cartella}/risorse/${nome}`];
      if (!url) throw new Error(`risorsa mancante: ${cartella}/risorse/${nome}`);
      const img = new Image();
      img.decoding = "async";
      img.src = url;
      await img.decode();
      return img;
    },
  };
}

export async function caricaFiltro(id: string): Promise<FiltroCaricato> {
  const f = perId.get(id);
  if (!f) throw new Error(`filtro non presente nell'app: ${id}`);
  const [modulo, manifest] = await Promise.all([MODULI[`${f.cartella}/filtro.ts`]!(), MANIFESTI[`${f.cartella}/manifest.json`]!()]);
  return { crea: modulo.default, manifest: manifest.default, risorse: risorseDi(f.cartella) };
}

export async function caricaManifest(id: string): Promise<ManifestFiltro | null> {
  const f = perId.get(id);
  const carica = f && MANIFESTI[`${f.cartella}/manifest.json`];
  return carica ? (await carica()).default : null;
}

const cacheTesti = new Map<string, Promise<Record<string, [string, string]>>>();

function testiLingua(lingua: string): Promise<Record<string, [string, string]>> {
  const carica = TESTI_INDICE[`./indice/testi.${lingua}.json`] ?? TESTI_INDICE["./indice/testi.en.json"];
  if (!carica) return Promise.resolve({});
  if (!cacheTesti.has(lingua)) {
    const p = carica().then((m) => m.default);
    p.catch(() => cacheTesti.delete(lingua)); // un errore di rete non resta in memoria: si riprova la prossima volta
    cacheTesti.set(lingua, p);
  }
  return cacheTesti.get(lingua)!;
}

/** Nome e descrizione nella lingua chiesta (ripiego: inglese, poi l'id). Un solo file per lingua per tutti i filtri. */
export async function testiFiltro(id: string, lingua: string): Promise<{ nome: string; descrizione: string }> {
  const t = (await testiLingua(lingua))[id] ?? (lingua !== "en" ? (await testiLingua("en"))[id] : undefined);
  return t ? { nome: t[0], descrizione: t[1] } : { nome: id, descrizione: "" };
}
